import type { ReactElement } from 'react';
import { Check, ExternalLink, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type {
  AggregatedPackage,
  OsvCellSummary,
} from '@kirin/api-client/contracts/agent-command-events';
import { Severity } from '@kirin/api-client/contracts/shared/severity';
import {
  formatCvssChipText,
  osvAdvisoryUrl,
  OSV_SEVERITY_CLASSES,
} from '@/lib/osv';

/**
 * "Vulnerabilities (OSV)" cell for the Packages tab (KIR-3805). Renders the
 * OSV summary the backend derives from the Kirin mirror: severity chip
 * (level + CVSS score, colored by band), fix-status chip, published date, an
 * "OSV ↗" link, a "+N more" chip, a red "Malicious" label, and the
 * loading / "OSV unavailable" / "No known vulnerabilities" states.
 */

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** "12 Jun 2026" — date only, matching the ticket's format. */
function formatPublished(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

function OsvLink({ id }: { id: string }): ReactElement {
  return (
    <a
      href={osvAdvisoryUrl(id)}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-0.5 text-primary hover:underline"
      onClick={e => e.stopPropagation()}
    >
      OSV <ExternalLink className="h-3 w-3" />
    </a>
  );
}

function SeverityChip({
  summary,
}: {
  summary: NonNullable<OsvCellSummary['highest']>;
}): ReactElement {
  if (summary.malicious) {
    return (
      <Badge
        variant="destructive"
        className="uppercase"
        aria-label="Malicious package"
      >
        Malicious
      </Badge>
    );
  }
  // Unknown severity (no CVSS and no qualitative level) → neutral chip, never a
  // scary "High" (PR #2158 review, F5).
  if (summary.level == null) {
    return (
      <Badge variant="outline" className="text-muted-foreground">
        Unknown
      </Badge>
    );
  }
  const level = summary.level as Severity;
  return (
    <Badge variant="outline" className={OSV_SEVERITY_CLASSES[level]}>
      {formatCvssChipText(level, summary.score)}
    </Badge>
  );
}

function FixChip({ fixAvailable }: { fixAvailable: boolean }): ReactElement {
  return fixAvailable ? (
    <Badge
      variant="outline"
      className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800"
    >
      Fix available
    </Badge>
  ) : (
    <Badge
      variant="outline"
      className="bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800"
    >
      No fix available
    </Badge>
  );
}

export function OsvVulnerabilityCell({
  row,
}: {
  row: AggregatedPackage;
}): ReactElement {
  const osv = row.osv;

  // Not yet checked.
  if (osv == null) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Checking OSV…
      </span>
    );
  }

  if (osv.status === 'error') {
    return <span className="text-muted-foreground">OSV unavailable</span>;
  }

  if (osv.status === 'none' || osv.highest == null) {
    return (
      <span className="inline-flex items-center gap-1 text-muted-foreground">
        <Check className="h-3.5 w-3.5" />
        No known vulnerabilities
      </span>
    );
  }

  const { highest, count } = osv;
  const published = formatPublished(highest.published);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-1">
        <SeverityChip summary={highest} />
        <FixChip fixAvailable={highest.fixAvailable} />
        {count > 1 && (
          <Badge variant="outline" className="text-muted-foreground">
            +{count - 1} more
          </Badge>
        )}
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        {published && <span>Published {published}</span>}
        <OsvLink id={highest.id} />
      </div>
    </div>
  );
}

OsvVulnerabilityCell.displayName = 'OsvVulnerabilityCell';
